// Workspace 2 shared pieces: the engagement's workspace tables (wp_index, attention, pbc_index, wp_messages) loaded once per
// engagement, the act drawer for any engagement, and the small visual parts every screen uses (progress bar, counters, filter
// chips, sign-off marks, waiting icons, sources). Everything shown is the engine's; counts are counts of its rows.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ActionDrawer, type ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { Button, Icon, IssueStatusChip, type IconName } from "../../components";
import type { AttentionIssue, AttentionItem, EngagementHeader, FileNode, PbcIndexRow, TeamMember, WpIndexRow, WpMessage, WpSignoffMark, WsSource } from "../../data/types";
import { mayRequest } from "../../lib/commands";
import { fmtDate, initials } from "../../lib/format";
import { useLoad, usePoll, useRealtime } from "../../lib/hooks";
import { issueHref, issueKeyOf } from "../../lib/issues";
import { reconcile, settleKey, useSettled, visibleItems } from "../../lib/settled";
import { subkindLabel } from "../../lib/workspace";

export interface Ws {
  id: string; header: EngagementHeader; team: TeamMember[];
  wp: WpIndexRow[]; attention: AttentionItem[]; pbc: PbcIndexRow[]; messages: WpMessage[];
  /** the engine's folder tree (file.tree): the files each folder holds and each Evidence folder's documents; [] when not published */
  files: FileNode[];
  loading: boolean; error?: string; reload: () => void;
}
const Ctx = createContext<Ws | null>(null);
export function useWs(): Ws {
  const c = useContext(Ctx);
  if (!c) throw new Error("useWs outside the engagement workspace");
  return c;
}

/** Loads the four workspace tables of one engagement; refreshes on the `workspace` Realtime topic. */
export function WsProvider({ id, header, team, children }: { id: string; header: EngagementHeader; team: TeamMember[]; children: ReactNode }) {
  const { ds } = useApp();
  const t = useLoad(() => Promise.all([ds.table(id, "wp_index"), ds.table(id, "attention"), ds.table(id, "pbc_index"), ds.table(id, "wp_messages"),
    ds.table(id, "file_nodes").catch(() => [] as FileNode[])]), [ds, id]);                    // the folder view's files: optional, never blocks the workspace
  useRealtime(ds, "workspace", (e) => { if (!e.engagementId || e.engagementId === id) t.reload(); });
  useRealtime(ds, "inbox", (e) => { if (e.row?.status === "done" && (!e.engagementId || e.engagementId === id)) t.reload(); });   // an act finished: read again
  usePoll(ds, t.reload);
  const [wp, raw, pbc, messages, files] = t.data ?? [[], [], [], [], []];
  const settled = useSettled();
  const attention = useMemo(() => visibleItems(raw, settled), [raw, settled]);   // an item acted on from here leaves once the engine did it
  useEffect(() => { if (t.data) reconcile(t.data[1], [id]); }, [t.data, id]);
  return <Ctx.Provider value={{ id, header, team, wp, attention, pbc, messages, files, loading: t.loading && !t.data, error: t.error, reload: t.reload }}>{children}</Ctx.Provider>;
}

/** Workspace tables across several engagements (Home, a client, the firm-wide Action center). */
export function useManyWs(ids: string[]) {
  const { ds } = useApp();
  const key = ids.join("|");
  const r = useLoad(async () => {
    const out = await Promise.all(ids.map(async (id) => {
      const [attention, pbc] = await Promise.all([ds.table(id, "attention").catch(() => []), ds.table(id, "pbc_index").catch(() => [])]);
      return [id, { attention, pbc }] as const;
    }));
    return new Map(out);
  }, [ds, key]);
  useRealtime(ds, "workspace", () => r.reload());
  useRealtime(ds, "inbox", (e) => { if (e.row?.status === "done") r.reload(); });
  usePoll(ds, r.reload);
  useEffect(() => { if (r.data) reconcile([...r.data.values()].flatMap((x) => x.attention), [...r.data.keys()]); }, [r.data]);
  return r;
}

/** One act drawer for any engagement: `open({command, engagementId, fixed, title})`. */
export function useWsAct(onSent?: () => void) {
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  return { open: (i: ActionIntent) => setIntent(i), drawer: <ActionDrawer intent={intent} onClose={() => setIntent(null)} onSent={onSent} /> };
}

/** Where an item without a command opens: its issue page, its tailoring review, the client document, or its workpaper. */
export function itemHref(item: AttentionItem): string {
  const p = item.action.payload;
  if (typeof p.issue === "string") return issueHref(item.engagementId, p.issue);
  if (typeof p.tailoring === "string") return `/e/${item.engagementId}/tailoring/${encodeURIComponent(p.tailoring)}`;
  const doc = p.pbc ?? (item.tab === "client_request" ? p.id : undefined);
  if (doc !== undefined && doc !== null) return `/e/${item.engagementId}/documents#pbc-${String(doc)}`;
  if (p.ref) return `/e/${item.engagementId}/wp/${encodeURIComponent(String(p.ref))}`;
  if (item.ref) return `/e/${item.engagementId}/wp/${encodeURIComponent(item.ref)}`;
  return `/e/${item.engagementId}/actions`;
}

/** The full explanation page of a flagged issue or a tailoring review, when the item has one. */
export function detailHref(item: AttentionItem): string | null {
  const k = issueKeyOf(item.key);
  if (k) return issueHref(item.engagementId, k);
  if (typeof item.action.payload.tailoring === "string") return itemHref(item);
  return null;
}

/** The one action an attention item offers: its command's form (fixed fields from the engine), or a link when it has none. Once
 * the act is sent from here the item shows it is waiting for the engine (no second act), then the engine's refusal if it refused. */
export function ItemAction({ item, open, engagementName }: { item: AttentionItem; open: (i: ActionIntent) => void; engagementName?: string }) {
  const { catalogue, me } = useApp();
  const settled = useSettled().get(settleKey(item.engagementId, item.key));
  const label = item.action.label || "Open";
  if (!item.action.command) {
    return <Link to={itemHref(item)} className="inline-flex min-h-7 items-center rounded-md border border-line bg-raised px-2 text-xs font-medium hover:bg-sunken" aria-label={`${label}: ${item.title}`}>{label}</Link>;
  }
  const spec = catalogue.get(item.action.command);
  const ok = mayRequest(spec, me, "engagement");
  const waiting = settled && ["pending", "running", "pending_signature"].includes(settled.state);
  const refused = settled && (settled.state === "refused" || settled.state === "failed");
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button size="sm" kind="secondary" disabled={!ok.ok || !!waiting} title={waiting ? "Sent — waiting for the engine" : ok.reason ?? undefined}
        aria-label={`${label}: ${item.title}${ok.ok ? "" : ` (not available: ${ok.reason})`}${waiting ? " (sent — waiting for the engine)" : ""}`}
        data-command={item.action.command}
        onClick={() => open({ command: item.action.command!, engagementId: item.engagementId, fixed: item.action.payload, settles: settleKey(item.engagementId, item.key),
          title: `${label}: ${item.title}${engagementName ? ` (${engagementName})` : ""}` })}>
        {waiting ? "Sent…" : label}
      </Button>
      {waiting && <span role="status" className="text-[11px] text-muted">Sent — waiting for the engine</span>}
      {refused && <span role="alert" className="max-w-56 text-left text-[11px] text-blocked">{settled.state === "failed" ? "The engine failed" : "Not done"}: {settled.note}</span>}
    </span>
  );
}

/** A bar for a figure the engine counted (final of total). */
export function ProgressBar({ done, total, label, className = "" }: { done: number; total: number; label: string; className?: string }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <span className={`flex items-center gap-2 ${className}`}>
      <span role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-valuetext={`${pct}% — ${done} of ${total}`}
        className="h-1.5 min-w-12 flex-1 overflow-hidden rounded-full bg-sunken ring-1 ring-line">
        <span className="block h-full rounded-full bg-done" style={{ width: `${pct}%` }} />
      </span>
      <span className="shrink-0 text-xs tabular-nums text-muted">{pct}%</span>
    </span>
  );
}

/** A big counter that opens a filtered list. */
export function Counter({ value, label, to, tone = "neutral", icon }: { value: number; label: string; to: string; tone?: "neutral" | "blocked" | "waiting" | "agent"; icon: IconName }) {
  const cls = { neutral: "text-ink", blocked: "text-blocked", waiting: "text-waiting", agent: "text-agent" }[tone];
  return (
    <Link to={to} className="flex items-center gap-3 rounded-lg border border-line bg-raised px-4 py-3 hover:border-brand" aria-label={`${value} ${label}`}>
      <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sunken ${cls}`}><Icon name={icon} /></span>
      <span className="min-w-0"><span className={`block text-2xl font-semibold tabular-nums ${cls}`}>{value}</span><span className="block text-xs text-muted">{label}</span></span>
    </Link>
  );
}

/** A row of toggle chips (single choice), each with an optional count. */
export function Chips<T extends string>({ label, options, value, onChange, className = "" }: {
  label: string; options: Array<{ id: T; label: string; count?: number }>; value: T; onChange: (v: T) => void; className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={`flex flex-wrap gap-1.5 ${className}`}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${value === o.id ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line bg-raised hover:bg-sunken"}`}>
          {o.label}{o.count !== undefined && <span className="tabular-nums text-muted">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** A segmented "Group by" switch. */
export function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted" id={`seg-${label.replace(/\W+/g, "-")}`}>{label}</span>
      <div role="group" aria-labelledby={`seg-${label.replace(/\W+/g, "-")}`} className="inline-flex overflow-hidden rounded-md border border-line">
        {options.map((o, i) => (
          <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}
            className={`px-2.5 py-1 ${i ? "border-l border-line" : ""} ${value === o.id ? "bg-brand text-brand-ink" : "bg-raised hover:bg-sunken"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

const RUNGS: Array<[keyof WpIndexRow["signoffs"], string, string]> = [["preparer", "P", "Prepared"], ["reviewer", "R", "Reviewed"], ["partner", "Pt", "Partner"], ["eqr", "EQR", "EQR"]];
/** Sign-off circles P · R · Pt (+ EQR when the engine lists one), filled from the engine's marks. */
export function SignMarks({ s }: { s: WpIndexRow["signoffs"] }) {
  return (
    <span className="inline-flex gap-1" role="list" aria-label="Sign-offs">
      {RUNGS.map(([k, short, long]) => {
        const m = s[k] as WpSignoffMark | null;
        if (!m) return null;
        const label = m.done ? `${long}: ${m.who ?? "signed"}${m.at ? `, ${fmtDate(m.at)}` : ""}` : `${long}: not yet`;
        return (
          <span key={k} role="listitem" aria-label={label} title={label}
            className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full border px-1 text-[10px] font-semibold ${m.done ? "border-done bg-done text-paper" : "border-line text-muted"}`}>
            {m.done && m.who ? initials(m.who).slice(0, 2) : short}
          </span>
        );
      })}
    </span>
  );
}

/** What a workpaper waits on, as small labelled icons (client document, open question, flagged issue). */
export function WaitIcons({ w }: { w: WpIndexRow }) {
  const icons: Array<[IconName, string, string]> = [];
  if (w.waitingOn?.kind === "client") icons.push(["clip", `Waiting on client: ${w.waitingOn.ref} ${w.waitingOn.label}`, "text-client"]);
  if (w.waitingOn && w.waitingOn.kind !== "client") icons.push(["alert", `Waiting on ${w.waitingOn.kind === "workpaper" ? "workpaper" : "a decision"}: ${w.waitingOn.label}`, "text-waiting"]);
  if (w.counts.questions) icons.push(["help", `${w.counts.questions} open question${w.counts.questions === 1 ? "" : "s"}`, "text-agent"]);
  if (w.counts.issues) icons.push(["flag", `${w.counts.issues} flagged issue${w.counts.issues === 1 ? "" : "s"}`, "text-blocked"]);
  if (w.stale) icons.push(["alert", `Out of date: ${w.staleWhy ?? "an input changed"}`, "text-stale"]);
  if (!icons.length) return <span className="text-xs text-muted" aria-label="Nothing waiting">—</span>;
  return (
    <span className="inline-flex gap-1">
      {icons.map(([n, label, cls], i) => <span key={i} role="img" aria-label={label} title={label} className={cls}><Icon name={n} size={16} /></span>)}
    </span>
  );
}
export function WaitLegend() {
  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted" aria-label="Legend">
      <span className="inline-flex items-center gap-1"><span className="text-client"><Icon name="clip" size={14} /></span>Waits on a client document</span>
      <span className="inline-flex items-center gap-1"><span className="text-agent"><Icon name="help" size={14} /></span>Open question</span>
      <span className="inline-flex items-center gap-1"><span className="text-blocked"><Icon name="flag" size={14} /></span>Flagged issue</span>
      <span className="inline-flex items-center gap-1"><span className="text-waiting"><Icon name="alert" size={14} /></span>Waits on a workpaper or decision</span>
    </p>
  );
}

export function SubkindChip({ subkind }: { subkind: string }) {
  return <span className="inline-flex rounded border border-line bg-sunken px-1.5 py-0.5 text-[11px] text-muted">{subkindLabel(subkind)}</span>;
}

export function Sources({ sources }: { sources: WsSource[] }) {
  if (!sources.length) return null;
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Sources">
      {sources.map((s, i) => <li key={i} className="rounded border border-line px-1.5 py-0.5 text-[11px] text-muted" title={s.ref}>{s.label}</li>)}
    </ul>
  );
}

/** Engine internals, closed by default. */
export function EngineDetails({ children, label = "Engine details" }: { children: ReactNode; label?: string }) {
  return (
    <details className="rounded-md border border-line bg-raised text-sm">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted hover:text-ink">{label}</summary>
      <div className="border-t border-line px-3 py-2 text-xs text-muted">{children}</div>
    </details>
  );
}

/** The status chip of an Action center row's issue (ADR-0034) — the same chip as on the issue page and the history. */
export function AttentionIssueChip({ issue }: { issue: AttentionIssue | null | undefined }) {
  if (!issue) return null;
  return <IssueStatusChip issue={{ status: issue.status, chip: issue.chip ?? null, state: issue.state ?? null, status_label: issue.statusLabel }} />;
}

/** "You" when it is the signed-in person. */
export function Who({ name }: { name: string | null }) {
  const { me } = useApp();
  if (!name) return <span className="text-muted">—</span>;
  if (name === "agent") return <span className="inline-flex items-center gap-1 text-agent"><span aria-hidden="true">✦</span>Agent</span>;
  return <span>{name === me.personName ? "You" : name}</span>;
}
